import React, { useState, useEffect, useCallback } from 'react';
import './index.css';
import LoginPage from './components/auth/LoginPage';
import Layout from './components/shared/Layout';
import Dashboard from './components/dashboard/Dashboard';
import CheckInPage from './components/reservations/CheckInPage';
import ReservationBookingPage from './components/reservations/ReservationBookingPage';
import BulkBookingPage from './components/reservations/BulkBookingPage';
import CheckOutPage from './components/reservations/CheckOutPage';
import ReservationsPage from './components/reservations/ReservationsPage';
import GuestDirectory from './components/guests/GuestDirectory';
import ExpensesPage from './components/expenses/ExpensesPage';
import HotelManagementPage from './components/admin/HotelManagementPage';
import RoomGridPage from './components/dashboard/RoomGridPage';
import ShiftPage from './components/admin/ShiftPage';
import EnquiryPage from './components/crm/EnquiryPage';
import SuperAdminTools from './components/admin/SuperAdminTools';
import DocumentsPage from './components/admin/DocumentsPage';
import DrawerAuditPage from './components/admin/DrawerAuditPage';
import NightAuditPage from './components/reservations/NightAuditPage';
import GuestRegisterPage from './components/admin/GuestRegisterPage';
import MultiHotelDashboard from './components/admin/MultiHotelDashboard';
import CalendarPage from './components/calendar/CalendarPage';
import InventoryPage from './components/admin/InventoryPage';
import LaundryPage from './components/admin/LaundryPage';
import ReportsPage from './components/admin/ReportsPage';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ConfirmProvider } from './context/ConfirmContext';

// ─── TOAST HOOK ───────────────────────────────────────
function useToast() {
  const [toast, setToast] = useState({ msg: '', type: '', show: false });
  const showToast = useCallback((msg, type = 'success') => {
    setToast({ msg, type, show: true });
    setTimeout(() => setToast(t => ({ ...t, show: false })), 6000);
  }, []);
  return { toast, showToast };
}

function AccessDenied() {
  return (
    <div className="empty" style={{ marginTop: 60 }}>
      <div className="empty-icon">🔒</div>
      <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>Access Denied</div>
      <div style={{ fontSize: 13, color: 'var(--muted)' }}>You don't have permission to view this page.</div>
    </div>
  );
}

// ─── INNER APP (uses AuthContext) ─────────────────────
function AppInner() {
  const { user, hotel: authHotel, loading } = useAuth();
  const [page, setPage] = useState('dashboard');
  const [hotel, setHotel] = useState(null);
  const { toast, showToast } = useToast();
  const [selectionResId, setSelectionResId] = useState(null);
  const [expiredSession, setExpiredSession] = useState(false);

  // When auth hotel changes (e.g. login), sync our local hotel state
  useEffect(() => {
    if (authHotel) setHotel(authHotel);
  }, [authHotel]);

  useEffect(() => {
    const handleSessionExpired = () => {
      setExpiredSession(true);
    };
    window.addEventListener('session-expired', handleSessionExpired);
    return () => window.removeEventListener('session-expired', handleSessionExpired);
  }, []);

  const navigateToCheckout = (resId) => {
    setSelectionResId(resId);
    setPage('checkout');
  };

  const navigateToEdit = (resId) => {
    setSelectionResId(resId);
    setPage('reservations');
  };

  const backToDashboard = () => {
    setSelectionResId(null);
    setPage('dashboard');
  };

  const clearSelection = () => setSelectionResId(null);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
        <div style={{ color: 'var(--muted)', fontSize: 13 }}>Loading…</div>
      </div>
    );
  }

  if (!user) {
    return (
      <LoginPage onLogin={({ user: u, hotel: h }) => {
        if (h) setHotel(h);
        window.location.reload(); // Let AuthContext re-read sessionStorage
      }} />
    );
  }

  const renderPage = () => {
    // If no hotel selected, superadmins default to the Global Dashboard
    if (!hotel && user.role === 'superadmin' && page === 'dashboard') {
      return <MultiHotelDashboard onNavigate={setPage} />;
    }

    if (!hotel && page !== 'guests' && page !== 'hotels' && page !== 'users' && page !== 'supertools' && page !== 'multigrid' && page !== 'documents') {
      // Only superadmin has a hotel switcher in the sidebar (Layout.js) — a staff/
      // manager account with no hotel_id assigned has no way to act on "select a
      // property," since the backend only ever returns hotels matching their own
      // hotel_id (zero, when it's null). That combination was a silent dead end;
      // tell them what's actually wrong and who can fix it instead.
      const canSelectHotel = user.role === 'superadmin';
      return (
        <div className="empty" style={{ marginTop: 60 }}>
          <div className="empty-icon">🏨</div>
          <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>
            {canSelectHotel
              ? 'Select a property from the sidebar to view detailed operations'
              : 'Your account isn’t assigned to a hotel yet'}
          </div>
          {!canSelectHotel && (
            <div style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 360, margin: '0 auto' }}>
              Ask a superadmin to assign you to a property from Hotel Management → Users. You won't be able to check guests in/out or access other operations until then.
            </div>
          )}
        </div>
      );
    }
    switch (page) {
      case 'dashboard': return <Dashboard hotel={hotel} onNavigate={setPage} onCheckout={navigateToCheckout} onEdit={navigateToEdit} />;
      case 'checkin': return <CheckInPage hotel={hotel} onSuccess={showToast} onNavigate={setPage} />;
      case 'prebooking': return <ReservationBookingPage hotel={hotel} onSuccess={showToast} />;
      case 'bulkbooking': return <BulkBookingPage hotel={hotel} onSuccess={showToast} />;
      case 'checkout': return <CheckOutPage hotel={hotel} onSuccess={showToast} initialResId={selectionResId} />;
      case 'reservations': return <ReservationsPage hotel={hotel} onSuccess={showToast} initialEditId={selectionResId} clearSelection={clearSelection} onBack={backToDashboard} />;
      case 'guests': return <GuestDirectory hotel={hotel} />;
      case 'inventory': return <InventoryPage hotel={hotel} onSuccess={showToast} />;
      case 'laundry': return <LaundryPage hotel={hotel} onSuccess={showToast} />;
      case 'reports': return <ReportsPage hotel={hotel} />;
      case 'expenses': return <ExpensesPage hotel={hotel} />;
      case 'hotelmanagement':
      case 'roomtypes':
      case 'rooms':
      case 'billsettings':
      case 'hotels':
      case 'users':
        // Layout.js's sidebar only ever links here for superadmin/manager, and the
        // backend independently enforces requireRole on every admin route — but
        // this page itself has no internal guard, so if `page` state ever ends up
        // here any other way, it would render the full admin UI rather than an
        // explicit denial. Belt-and-suspenders, not the primary access control.
        if (user.role !== 'superadmin' && user.role !== 'manager') return <AccessDenied />;
        return <HotelManagementPage onSuccess={showToast} />;
      case 'roomgrid': return <RoomGridPage hotel={hotel} onSuccess={showToast} onNavigate={setPage} onCheckout={navigateToCheckout} onEdit={navigateToEdit} />;
      case 'shifts': return <ShiftPage hotel={hotel} onSuccess={showToast} />;
      case 'followups': return <EnquiryPage hotel={hotel} onSuccess={showToast} />;
      case 'nightaudit': return <NightAuditPage hotel={hotel} onSuccess={showToast} onNavigate={setPage} />;
      case 'guestregister': return <GuestRegisterPage hotel={hotel} />;
      case 'draweraudit': return <DrawerAuditPage hotel={hotel} />;
      case 'calendar': return <CalendarPage hotel={hotel} />;
      case 'supertools':
        if (user.role !== 'superadmin') return <AccessDenied />;
        return <SuperAdminTools hotel={hotel} onSuccess={showToast} />;
      case 'documents':
        if (user.role !== 'superadmin') return <AccessDenied />;
        return <DocumentsPage />;
      case 'multigrid':
        if (user.role !== 'superadmin') return <AccessDenied />;
        return (
          <MultiHotelDashboard
            onNavigate={(targetPage, selectedHotel) => {
              if (selectedHotel && typeof selectedHotel === 'object') {
                setHotel(selectedHotel);
              }
              setPage(targetPage);
            }}
          />
        );
      default: return <Dashboard hotel={hotel} />;
    }
  };

  return (
    <Layout page={page} setPage={setPage} hotel={hotel} setHotel={setHotel}>
      {renderPage()}

      {/* SESSION EXPIRED POPUP */}
      {expiredSession && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          background: 'rgba(0, 0, 0, 0.4)', backdropFilter: 'blur(10px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 99999, padding: 20
        }}>
          <div style={{
            background: 'var(--bg)', borderRadius: 20, padding: 40,
            maxWidth: 450, width: '100%', boxShadow: '0 20px 40px rgba(0,0,0,0.3)',
            textAlign: 'center', border: '1px solid rgba(255,255,255,0.1)'
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
            <h2 style={{ color: 'var(--text)', marginBottom: 12, fontWeight: 700 }}>Security Refresh</h2>
            <p style={{ color: 'var(--muted)', fontSize: 16, lineHeight: 1.5, marginBottom: 32 }}>
              Your session has expired or your account was logged into from another device. For your security, please sign in again.
            </p>
            <button 
              onClick={() => {
                sessionStorage.removeItem('hms_token');
                sessionStorage.removeItem('hms_session');
                window.location.reload();
              }}
              style={{
                width: '100%', padding: '14px 24px', background: 'var(--gold)',
                color: '#fff', border: 'none', borderRadius: 12,
                fontSize: 16, fontWeight: 700, cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(200,151,42,0.3)',
                transition: 'transform 0.1s'
              }}
              onMouseDown={e => e.currentTarget.style.transform = 'scale(0.98)'}
              onMouseUp={e => e.currentTarget.style.transform = 'scale(1)'}
            >
              Sign In Again
            </button>
          </div>
        </div>
      )}

      {/* TOAST */}
      <div className={`toast ${toast.show ? 'show' : ''} ${toast.type}`}>
        {toast.type === 'error' ? '❌' : '✅'} {toast.msg}
      </div>
    </Layout>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ConfirmProvider>
        <AppInner />
      </ConfirmProvider>
    </AuthProvider>
  );
}
